/**
 * Reading and writing a record's relationships, through the shapes
 * `record/relations.ts` derives (#484).
 *
 * Every hook here is entity-agnostic: it is handed a `RelationSlot` and does
 * what that slot says. There is no branch on `core_organization`, no
 * `rel_type: 'works_at'` literal, and nothing that needs editing when a module
 * registers a new relationship — which is the whole point, and the thing the
 * `crm/RecordPage` sections it replaces could not claim.
 *
 * Tier 1 — console/shared-experience wiring only. This file declares no SQL
 * policy, function, grant or vocabulary; it consumes registered relationship
 * metadata and the write doors already declared by core.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { McpToolSpec, Row, SemanticField } from '@solum/sdk'
import type { FormField, FilterFieldDef, LookupSection } from '@solum/ui'
import { useSolum } from '../data/client'
import { buildRpcArgs, deriveSearchTools, rowsToHits, type SearchTool } from '../app/federatedSearch'
import {
  displayLabel, SUBJECT_SEARCH_ONLY, useEntityTable, useIsSubjectEntity, useLookupLabels, useRelated, useSchema,
  useSemantics,
} from '../data/queries'
import { relationRead, relationshipWriteOffered, type RelationSlot } from './relations'
import { invalidateRecordSurfaces, reconcileRelationships } from '../data/queries'

/**
 * The other-end rows of one slot.
 *
 * `isError` is carried separately and deliberately: an empty list and a REFUSED
 * read are the same `rows: []` on screen, so a panel that only checks length
 * says "No contacts yet" when the truth is "we could not read them" — the #389
 * defect, one surface over. The caller must branch on it before it branches on
 * emptiness.
 */
export interface RelationRows {
  rows: RelationRow[]
  isLoading: boolean
  isError: boolean
  error: unknown
}

/**
 * One displayed relationship row.
 *
 * `record` is the far endpoint. `edge` is the governed relationship row which
 * says WHY it is here and carries its own attributes and validity. FK-backed
 * relations have no edge row and therefore carry `null`.
 */
export interface RelationRow {
  record: Row
  edge: Row | null
}

export interface LinkRelationshipInput {
  otherId: string
  edgeAttributes?: Row
  validFrom?: string | null
}

/**
 * Join visible edge rows to visible endpoint rows without treating the graph as
 * authority. If RLS withholds the endpoint, the edge is not enough to invent a
 * row for it; if two real edge rows reach one endpoint, both remain real rows.
 */
export function pairEdgeRows(
  edgeRows: readonly { edge: Row; otherEnd: 'from_id' | 'to_id' }[],
  endpoints: ReadonlyMap<string, Row>,
  excludeEndpointId?: string,
): RelationRow[] {
  const out: RelationRow[] = []
  for (const { edge, otherEnd } of edgeRows) {
    const id = String(edge[otherEnd] ?? '')
    if (!id || id === excludeEndpointId) continue
    const record = endpoints.get(id)
    if (record) out.push({ record, edge })
  }
  return out
}

/**
 * The rows on the other end of a slot.
 *
 * Two shapes, one result. An EDGE read fetches `core.relationship` and then the
 * referenced rows by id (never a bounded page of the whole entity — the mistake
 * `useLookupLabels`' own comment records, where a 500-row pool silently printed
 * the wrong thing past 500 organizations). An FK read goes straight at the child
 * table, filtered on the column that points back here.
 *
 * Both branches' hooks are called unconditionally and gated by `enabled`: React
 * requires a stable hook count, and a slot can change under the user when they
 * switch tabs.
 */
export function useRelationRows(slot: RelationSlot | null, recordId: string): RelationRows {
  const read = slot ? relationRead(slot, recordId) : null
  const isEdge = read?.via === 'edge'
  // A SYMMETRIC self-referential edge is stored on whichever end the person who
  // made the link happened to use, so it has to be read from both. Second hook
  // called unconditionally and gated by `enabled`, like the others — React needs
  // a stable hook count and the slot changes under the user when they switch tab.
  const isBoth = read?.via === 'edge-both'

  const edges = useRelated('core', 'relationship',
    isEdge ? read.where : isBoth ? read.ends[0].where : {}, isEdge || isBoth)
  const edgesB = useRelated('core', 'relationship', isBoth ? read.ends[1].where : {}, isBoth)

  const edgePairs = useMemo(() => {
    if (isEdge) {
      return ((edges.data ?? []) as Row[]).map((edge) => ({ edge, otherEnd: read.otherEnd }))
    }
    if (!isBoth) return []
    // The EDGE is the row. Two stored edges between the same endpoints may
    // carry different attributes or validity windows, so collapsing them by
    // endpoint would erase governed data. A self-link is still omitted: it is
    // this record, not a row on the other side of the band.
    const out: { edge: Row; otherEnd: 'from_id' | 'to_id' }[] = []
    for (const [rows, otherEnd] of [
      [(edges.data ?? []) as Row[], read.ends[0].otherEnd] as const,
      [(edgesB.data ?? []) as Row[], read.ends[1].otherEnd] as const,
    ]) {
      for (const edge of rows) out.push({ edge, otherEnd })
    }
    return out
  }, [isEdge, isBoth, edges.data, edgesB.data, read, recordId])
  const edgeIds = useMemo(
    () => edgePairs.map(({ edge, otherEnd }) => String(edge[otherEnd] ?? '')).filter(Boolean),
    [edgePairs])
  const edgeRows = useLookupLabels(slot?.otherEntity ?? '', edgeIds)

  const fkTable = useEntityTable(read?.via === 'fk' ? read.entity : '')
  const fkRows = useRelated(
    fkTable?.schema_name ?? 'core', fkTable?.table_name ?? 'entity_type',
    read?.via === 'fk' ? read.where : {},
    read?.via === 'fk' && fkTable != null)

  if (!read) return { rows: [], isLoading: false, isError: false, error: null }
  if (read.via === 'fk') {
    return {
      rows: ((fkRows.data ?? []) as Row[]).map((record) => ({ record, edge: null })),
      isLoading: fkRows.isLoading, isError: fkRows.isError, error: fkRows.error,
    }
  }
  // Preserve the edge rows' own order. The endpoint lookup only supplies the
  // visible far-side record; it never becomes a second source of relationships.
  const rows = pairEdgeRows(edgePairs, edgeRows.byId, isBoth ? recordId : undefined)
  return {
    rows,
    // The second end counts in BOTH states. A refused read on one end with an
    // empty other end would otherwise report "no partners" — the #389 defect
    // this file's own header warns about, one hook along.
    isLoading: edges.isLoading || (isBoth && edgesB.isLoading)
      || (edgeIds.length > 0 && edgeRows.query.isLoading),
    isError: edges.isError || (isBoth && edgesB.isError) || edgeRows.query.isError,
    error: edges.error ?? (isBoth ? edgesB.error : null) ?? edgeRows.query.error,
  }
}

/**
 * Link an EXISTING record through a slot — the write half of the derivation.
 *
 * The host sends one finite envelope to `core.mutate_relationship`; the type's
 * stored contract selects the generic or governed handler. The browser neither
 * chooses a door nor falls back to raw edge/FK writes. The server refuses an FK
 * write from the field side rather than overwriting the record already open.
 */
export function useLinkRelation(slot: RelationSlot | null, recordId: string) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    // #877, shape 3: this PATCHes an arbitrary entity's table from the relations
    // band EVERY entity gets, so it can reach a table whose DML has been revoked
    // (`write_door: 'rpc'`) and be answered 403. `RelationAdd` calls `.mutate`,
    // so there was no rejection to notice and nothing on screen — the link
    // simply did not appear. No local handler, so the mutation-cache net in
    // `data/client.tsx` speaks; `what` is what it calls the thing.
    meta: { what: slot ? `the ${slot.label.toLowerCase()} link` : 'this link' },
    mutationFn: async ({ otherId, edgeAttributes = {}, validFrom = null }: LinkRelationshipInput) => {
      if (!slot || slot.kind === 'field' || !relationshipWriteOffered(slot, 'link')) {
        throw new Error('This relationship does not offer linking an existing record.')
      }
      return client.raw().rpcScalar<Row>('mutate_relationship', {
        p_operation: 'link', p_rel_type: slot.relType, p_direction: slot.direction,
        p_self_type: slot.selfEntity, p_self_id: recordId,
        p_other_type: slot.otherEntity, p_other_id: otherId,
        p_edge: { attributes: edgeAttributes, valid_from: validFrom },
      }, 'core')
    },
    /* Both halves of the read model, AWAITED and scoped to this record (#2000).
       Refreshing only the rows leaves a successful existing-link hidden behind
       a stale zero badge; refreshing them without awaiting lets the picker
       close before either has landed. `reconcileRelationships` is returned, so
       a caller using `mutateAsync` does not resolve until the band and its
       heading agree.

       The fk branch PATCHes a row of the OTHER entity, so that entity's own
       surfaces are stale too — the same mirror `useCreateRelated` below
       already carries for the create path (#2018). It was missing here, which
       made an existing-link the one write that moved a row without telling
       any grid of that entity about it. */
    /* BOTH ends (#2000 audit). An edge is a fact about two records, so linking
       moves the far record's bands and counts exactly as much as it moves this
       one's — and the far record may well be open in another pane. `otherId` is
       the mutation's own variable, so nothing has to be looked up to know it. */
    onSuccess: (_result, { otherId }) => {
      const labels = qc.invalidateQueries({ queryKey: ['lookup-labels'] })
      if (!slot) return labels
      return Promise.all([
        labels,
        invalidateRecordSurfaces(qc, slot.otherEntity),
        reconcileRelationships(qc, slot.selfEntity, recordId),
        reconcileRelationships(qc, slot.otherEntity, otherId),
      ]).then(() => undefined)
    },
  })
}

/**
 * Create a NEW record of the slot's other entity, and link it — in ONE
 * transaction, server-side (core 2026080311).
 *
 * This used to be two client round trips: insert the row, then insert the edge.
 * A failure between them left a record with NO edge, and every surface that
 * reaches that record reaches it by relationship — so the row was invisible
 * from everywhere while still existing. One hardcoded path on one relationship
 * was survivable; quick-create on every slot at every depth is not.
 *
 * The generic registered handler delegates to `core.create_related`, which
 * stamps `owner_id` when the payload omits it. The compiled insert policy is
 * `owner_id = (select auth.uid())`, nothing else supplies it, and
 * `quickCreateFields` offers required + summary columns — owner_id is neither.
 */
export function useCreateRelated(slot: RelationSlot | null, recordId: string) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { what: slot ? `this ${slot.label.toLowerCase()}` : 'this record' },
    mutationFn: async ({ record, edgeAttributes }: { record: Row; edgeAttributes: Row }): Promise<Row> => {
      if (!slot) throw new Error('This relationship cannot be created from here.')
      if (slot.kind === 'field') {
        // The from-end of an fk is a column on the record the user has open.
        // The server refuses it too; saying so here avoids a round trip to be
        // told something the slot already knows.
        throw new Error(`${slot.label} is a field on this record — set it on the form instead.`)
      }
      if (!relationshipWriteOffered(slot, 'create')) {
        throw new Error('This relationship does not offer creating a related record.')
      }
      const result = await client.raw().rpcScalar<{ record?: Row; record_id?: string }>('mutate_relationship', {
        p_operation: 'create', p_rel_type: slot.relType, p_direction: slot.direction,
        p_self_type: slot.selfEntity, p_self_id: recordId,
        p_other_type: slot.otherEntity, p_record: record,
        p_edge: { attributes: edgeAttributes },
      }, 'core')
      return result.record ?? { id: result.record_id }
    },
    /* THE MIRROR (#2018). This refreshed the edge and its count but never
       the created record itself, so a nested create added a row that any
       grid of that entity kept not showing until a reload — the create
       succeeded and the list disagreed, which reads as "it did not save".

       `slot.otherEntity` is the entity that gained a row; the record the
       user has open gained an edge, which the three below already cover. */
    onSuccess: (created) => {
      const labels = qc.invalidateQueries({ queryKey: ['lookup-labels'] })
      if (!slot) return labels
      /* Scoped and AWAITED (#2000). The two broad families this replaces
         refetched every related list on screen, including those of records
         nobody touched, and resolved before any of them had landed.

         The CREATED record is the far end and gets the same treatment: it did
         not exist a moment ago, so nothing of its own is cached yet, but a
         surface already showing that entity is reconciled by the mirror. */
      const otherId = String((created as Row)?.id ?? '')
      return Promise.all([
        labels,
        invalidateRecordSurfaces(qc, slot.otherEntity),
        reconcileRelationships(qc, slot.selfEntity, recordId),
        otherId ? reconcileRelationships(qc, slot.otherEntity, otherId) : Promise.resolve(),
      ]).then(() => undefined)
    },
  })
}

/**
 * Update declared attributes and optional lifecycle facts through the
 * relationship type's advertised `update` operation. The dispatcher preserves
 * the caller's authority; this generic surface adds none of its own.
 */
export interface RelationshipEdgeUpdate {
  id: string
  attributes: Row
  valid_from?: unknown
  valid_to?: unknown
  /**
   * The record on the OTHER end of the edge, when the caller has it.
   *
   * An edge's facts render on both records' bands, so both need reconciling
   * (#2000 audit). This hook is handed an edge id and nothing else, so the far
   * id cannot be derived here — the band that drew the row knows it and passes
   * it. Omitted, only the initiating end is reconciled, which is honest rather
   * than silently half-done.
   */
  otherId?: string
}

/**
 * The two universal lifecycle facts on every edge. They remain optional: blank
 * values are persisted as NULL, and an end date can be cleared to make a
 * relationship current again. Relationship-specific facts still come only
 * from the registered attribute declaration above.
 */
export function relationshipLifecycleFields(): FormField[] {
  return [
    { name: 'valid_from', type: 'date', label: 'Start date', hint: 'Optional' },
    {
      name: 'valid_to', type: 'date', label: 'End date', hint: 'Optional — clear it to reopen the relationship',
      validate: (value, all) => {
        const from = String(all.valid_from ?? '')
        const to = String(value ?? '')
        return from && to && to < from ? 'End date must be on or after the start date' : null
      },
    },
  ]
}

/** Keep lifecycle columns out of the relationship's declared attributes. */
export function relationshipEdgePatch(update: RelationshipEdgeUpdate): Row {
  const optionalDate = (value: unknown) => value == null || value === '' ? null : String(value)
  return {
    attributes: update.attributes,
    valid_from: optionalDate(update.valid_from),
    valid_to: optionalDate(update.valid_to),
  }
}

/**
 * The record whose band is showing the edge is REQUIRED, because the
 * reconciliation is scoped to it (#2000).
 *
 * It was briefly optional, with a broad fire-and-forget fallback for callers
 * that had no slot in hand. There are none — and leaving the fallback in meant
 * the one thing a census cannot tolerate: a legal way to write a relationship
 * and refresh everything vaguely. Required is what makes "no writer invalidates
 * broadly" a property the suite can check rather than a habit.
 *
 * Editing `valid_to` does not move `core.related_counts` — that function counts
 * edges with no validity predicate — but it does change which rows the band
 * renders, and the count is reconciled anyway, because the day that predicate
 * is added this must not be the surface that lies about it.
 */
export function useUpdateRelationshipEdge(slot: RelationSlot | null, recordId: string) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { what: 'these relationship facts' },
    mutationFn: async (update: RelationshipEdgeUpdate) => {
      if (!slot || !relationshipWriteOffered(slot, 'update')) {
        throw new Error('This relationship does not offer editing its facts.')
      }
      return client.raw().rpcScalar<Row>('mutate_relationship', {
        p_operation: 'update', p_rel_type: slot.relType, p_direction: slot.direction,
        p_self_type: slot.selfEntity, p_self_id: recordId,
        p_other_type: slot.otherEntity, p_other_id: update.otherId ?? null,
        p_edge_id: update.id, p_edge: relationshipEdgePatch(update),
      }, 'core')
    },
    onSuccess: (_result, update) => {
      if (!slot) return undefined
      return Promise.all([
        reconcileRelationships(qc, slot.selfEntity, recordId),
        update.otherId ? reconcileRelationships(qc, slot.otherEntity, update.otherId) : Promise.resolve(),
      ]).then(() => undefined)
    },
  })
}

/** End a temporal edge through the relationship type's declared end verb. */
export function useEndRelationshipEdge(slot: RelationSlot | null, recordId: string) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { what: 'this relationship' },
    mutationFn: async ({ id, otherId, on }: { id: string; otherId?: string; on?: string }) => {
      if (!slot || !relationshipWriteOffered(slot, 'end')) {
        throw new Error('This relationship does not offer ending its tenure.')
      }
      return client.raw().rpcScalar<Row>('mutate_relationship', {
        p_operation: 'end', p_rel_type: slot.relType, p_direction: slot.direction,
        p_self_type: slot.selfEntity, p_self_id: recordId,
        p_other_type: slot.otherEntity, p_other_id: otherId ?? null, p_edge_id: id,
        p_edge: { valid_to: on ?? new Date().toISOString().slice(0, 10) },
      }, 'core')
    },
    onSuccess: (_result, input) => {
      if (!slot) return undefined
      return Promise.all([
        reconcileRelationships(qc, slot.selfEntity, recordId),
        input.otherId ? reconcileRelationships(qc, slot.otherEntity, input.otherId) : Promise.resolve(),
      ]).then(() => undefined)
    },
  })
}

/**
 * How many rows sit behind each of a record's slots — ONE round trip for all of
 * them (core 2026080311), keyed by `RelationSlot.id`.
 *
 * Three things the caller must respect, because they are three different
 * sentences and a surface that conflates them lies:
 *
 *   · a count of 0        — there is nothing there; offer the create.
 *   · an ABSENT slot      — unknown. The server derived no counter for it, or
 *                           the read was refused. Never draw this as zero: "none
 *                           yet, add one" over rows RLS is hiding invites a
 *                           duplicate of a record the user cannot see.
 *   · `capped`            — there are MORE than `n`; render `99+`, not `99`.
 *
 * The cap is why this is affordable at all: slm registers 149 relationship
 * types, and an exact count per slot is a sequential scan for a number nobody
 * reads past two digits.
 */
export interface RelatedCount { n: number; capped: boolean }

export function useRelatedCounts(entity: string, recordId: string, enabled = true) {
  const { client } = useSolum()
  const query = useQuery({
    queryKey: ['related-counts', entity, recordId],
    enabled: enabled && Boolean(entity) && Boolean(recordId),
    queryFn: () => client.raw().rpcTable<{ slot_id: string; n: number; capped: boolean }>(
      'related_counts', { p_entity_type: entity, p_id: recordId }, 'core'),
  })
  const bySlot = useMemo(() => {
    const m = new Map<string, RelatedCount>()
    for (const row of query.data ?? []) m.set(row.slot_id, { n: Number(row.n), capped: Boolean(row.capped) })
    return m
  }, [query.data])
  return {
    bySlot,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/** `14`, `99+`, or nothing at all when the count is unknown. */
export function countLabel(c: RelatedCount | undefined): string | null {
  if (!c) return null
  return c.capped ? `${c.n}+` : String(c.n)
}

/* ── the Lookup's configuration, derived from the semantic layer ───────────── */

const FORM_KIND: Record<string, FormField['type']> = {
  text: 'text', number: 'number', money: 'money', date: 'date',
  option: 'select', boolean: 'switch', ref: 'relation',
}
const FILTER_KIND: Record<string, NonNullable<FilterFieldDef['type']>> = {
  text: 'text', number: 'number', money: 'number', date: 'date', option: 'select',
}
// `selectable` travels with the option (#1075): an archived vocabulary entry
// keeps its label here so a historical value renders as itself, and the picker
// disables the item instead of offering it. Dropping the key would restore the
// blank-label defect one layer out from where it was fixed.
const opts = (f: SemanticField) =>
  ((f.options ?? []) as { value: string; label: string; selectable?: boolean }[])
    .map((o) => ({ value: o.value, label: o.label, selectable: o.selectable }))

/**
 * The quick-create form for a slot's other entity: its REQUIRED fields, plus its
 * summary fields, in registered order.
 *
 * Deliberately not "every field" — a create-in-flow that asks for forty columns
 * is one the user abandons — and deliberately not a hand-picked list per entity,
 * which is the hardcoding this replaces. Read-only fields are excluded: the
 * semantic layer already says they are not the user's to set.
 */
export function quickCreateFields(fields: SemanticField[]): FormField[] {
  return fields
    .filter((f) => !f.read_only && (f.required || f.is_summary))
    .sort((a, b) => Number(b.required ?? false) - Number(a.required ?? false) || a.sort - b.sort)
    .slice(0, 6)
    .map((f) => ({
      name: f.column_name,
      type: FORM_KIND[f.kind] ?? 'text',
      label: f.label,
      required: Boolean(f.required),
      ...(f.kind === 'option' ? { options: opts(f) } : {}),
    }))
}

/**
 * The FULL create form — every field the semantic layer says is the user's to
 * set, in registered order.
 *
 * The counterpart to `quickCreateFields`, and deliberately the same derivation
 * minus its two narrowings: no required/summary filter and no cap. Promoting
 * quick → full must not change WHICH fields mean what, only how many of them
 * are on screen — otherwise the promotion is a different form, and a draft
 * cannot survive it.
 */
export function fullCreateFields(fields: SemanticField[]): FormField[] {
  return fields
    .filter((f) => !f.read_only)
    .sort((a, b) => a.sort - b.sort)
    .map((f) => ({
      name: f.column_name,
      type: FORM_KIND[f.kind] ?? 'text',
      label: f.label,
      required: Boolean(f.required),
      ...(f.kind === 'option' ? { options: opts(f) } : {}),
    }))
}

/** Form fields for facts declared by this relationship type, never client vocabulary. */
export function relationshipAttributeFields(slot: RelationSlot | null, prefix = ''): FormField[] {
  if (!slot || slot.storage !== 'edge') return []
  return slot.attributes.map((attribute) => ({
    name: `${prefix}${attribute.key}`,
    type: attribute.options.length > 0 ? 'select' : (FORM_KIND[attribute.kind] ?? 'text'),
    label: attribute.label,
    ...(attribute.options.length > 0 ? {
      options: attribute.options.map((option) => ({
        value: String(option.value), label: option.label, selectable: option.selectable,
      })),
    } : {}),
  }))
}

/** The advanced-filter fields for a slot's other entity — every filterable column. */
export function advancedFilterFields(fields: SemanticField[]): FilterFieldDef[] {
  return fields
    .filter((f) => FILTER_KIND[f.kind])
    .sort((a, b) => a.sort - b.sort)
    .map((f) => ({
      name: f.column_name,
      label: f.label,
      type: FILTER_KIND[f.kind],
      ...(f.kind === 'option' ? { options: opts(f) } : {}),
    }))
}

/** Semantic fields for the other end of a slot — the source for both configs above. */
export function useRelationTargetFields(slot: RelationSlot | null) {
  const sem = useSemantics(slot?.otherEntity)
  return useMemo(() => (sem.data?.fields ?? []) as SemanticField[], [sem.data])
}

/* ── searching the other end: the step that stops a duplicate ──────────────── */

/** How many rows a relation lookup asks for — a picker, not a list. */
export const RELATION_SEARCH_LIMIT = 20

/**
 * The registered search doors, discovered exactly as ⌘K discovers them.
 *
 * Same query key as `CommandPalette`'s, so the two share one cached
 * `describe_tools()` read rather than each holding their own.
 */
export function useSearchTools(): SearchTool[] {
  const { client } = useSolum()
  const schema = useSchema()
  const tools = useQuery({
    queryKey: ['mcp-tools'],
    queryFn: () => client.describeTools().catch(() => null as McpToolSpec[] | null),
    staleTime: 60_000,
  })
  return useMemo(
    () => (schema.data && Array.isArray(tools.data) ? deriveSearchTools(tools.data, schema.data.entities) : []),
    [tools.data, schema.data])
}

/**
 * A SERVER-SIDE contains-filter over an entity's own text columns, for an
 * entity with no registered search door.
 *
 * PostgREST's `or=(a.ilike.*q*,b.ilike.*q*)` takes the whole predicate as one
 * unquoted expression, so the query is sanitised rather than escaped: `,`, `(`,
 * `)` and `"` would each change the shape of the filter, and `%`/`_`/`\` are
 * ilike metacharacters. Dropping them narrows what the user meant; leaving them
 * in would send a filter the server reads as something else entirely.
 *
 * Returns null when the query is empty or the entity declares no text column —
 * the caller then asks for an unfiltered page, which is what a picker opened
 * with nothing typed should show.
 */
export function textSearchFilter(fields: SemanticField[], query: string): { key: string; value: string }[] | null {
  const q = query.trim().replace(/[,()"%_\\]/g, ' ').replace(/\s+/g, ' ').trim()
  if (!q) return null
  const cols = fields.filter((f) => f.kind === 'text').map((f) => f.column_name)
  if (cols.length === 0) return null
  if (cols.length === 1) return [{ key: cols[0]!, value: `ilike.*${q}*` }]
  return [{ key: 'or', value: `(${cols.map((c) => `${c}.ilike.*${q}*`).join(',')})` }]
}

/**
 * Search the slot's other entity for records to link.
 *
 * ── Why this is the load-bearing half of "add through a relationship" ──────
 * `RelationAdd` puts search and create on one surface deliberately: *"search
 * the existing directory first — that is the dedupe step — and create only if
 * nothing matches."* A search that cannot find what exists turns that surface
 * into a duplicate factory, which is exactly what it did (#1075 F1). This
 * function's own docstring claimed it *"prefers the module's REGISTERED
 * `search_*` RPC"*; the body read **50 rows off the top of the table** and
 * filtered them in the browser. On any directory bigger than a demo it answered
 * "no such person" for people who are plainly there — the class `crm/
 * RecordPage` already paid for once and wrote down: *"a capped read that writes
 * bad data rather than merely showing less of it."*
 *
 * Now it does what it said:
 *  · a REGISTERED door (`core.search_person`, `crm.search_opportunity`, …) is
 *    used when the metamodel resolves one for the entity — ranked, whole-table,
 *    under the caller's own RLS, and it carries a detail line (where a person
 *    works and as what) which is what tells two people with one name apart;
 *  · otherwise a SERVER-SIDE `ilike` over the entity's declared text columns.
 *    13 modules register no search tool, and "no search tool" must not mean
 *    "cannot link" — but it must not mean "cannot find", either.
 */
export function useRelationSearch(slot: RelationSlot | null) {
  const { client } = useSolum()
  const table = useEntityTable(slot?.otherEntity ?? '')
  const entity = slot?.otherEntity ?? ''
  const tools = useSearchTools()
  const fields = useRelationTargetFields(slot)
  const tool = useMemo(() => tools.find((t) => t.entity === entity) ?? null, [tools, entity])
  const subject = useIsSubjectEntity(entity)

  return useMemo(() => async (query: string, _facet: string | null, _type: string | null): Promise<LookupSection[]> => {
    if (tool) {
      const rows = await client.raw().rpcTable<Row>(
        tool.fn, buildRpcArgs(tool, query.trim(), RELATION_SEARCH_LIMIT), tool.schema)
      return [{
        items: rowsToHits(Array.isArray(rows) ? rows : []).map((h) => ({
          id: h.id, primary: h.label, ...(h.detail ? { secondary: h.detail } : {}),
        })),
      }]
    }
    if (!table) return []
    // A SUBJECT target (#2444) with no registered search door: its door has no
    // `ilike`, and a filtered-in-the-browser page would disclose — and record
    // — rows nobody asked for. Words are refused in words; an empty query is
    // still the plain first page, through the door.
    if (subject && query.trim()) throw new Error(SUBJECT_SEARCH_ONLY)
    const filters = subject ? null : textSearchFilter(fields, query)
    const rows = await client.raw().select<Row>(table.schema_name, table.table_name, {
      limit: RELATION_SEARCH_LIMIT, ...(filters ? { filters } : {}),
    })
    return [{ items: rows.map((r) => ({ id: String(r.id), primary: displayLabel(r) })) }]
  }, [client, table, tool, fields, subject])
}
